import { Account, User } from 'shared/types'
import { generateFetchRequest } from 'clients/fetch'
import AppState from 'shared/app_state'

type LoginCredentials = { email: string; password: string; rememberMe: boolean }
type LoginResponse = { success?: boolean; resource?: User }
type Identity = { id: string; fullName?: string; avatart?: string; account: Account; user: User }

const authProvider = {
  login: async ({ email, password, rememberMe: remember_me }: LoginCredentials): Promise<void | User> => {
    const request = generateFetchRequest({
      body: JSON.stringify({
        user: { email, password, remember_me },
      }),
      method: 'POST',
      path: 'users/sign_in',
    })

    return fetch(request).then(async (response) => {
      const data = await response.json()
      if (response.status != 200) {
        throw new Error(data.message)
      }

      const { resource: user }: LoginResponse = data

      await AppState.setIdentity({ user })

      return user
    })
  },
  checkError: (error): Promise<void> => {
    // called when dataProvider returns an error
    // rejected promise automatically calls authProvider.logout()

    if (error && error.status && [401, 403].includes(error.status))
      return Promise.reject({
        message:
          'Your request could not be completed. Either your session ended or you are not authorized to view the requested resource. Please login to continue.',
      })

    return Promise.resolve()
  },
  checkAuth: (): Promise<void> => {
    // AUTHENTICATION
    // Checking Credentials During Navigation
    // called each time the user navigates to a list, edit, create or show page
    // rejected promise automatically calls authProvider.logout()

    if (AppState.getCurrentUser() != null) return Promise.resolve()
    return Promise.reject({ message: false })
  },
  getPermissions: (): Promise<string[]> => {
    // AUTHORIZATION

    return Promise.resolve(AppState.getRoles())
  },
  getIdentity: (): Promise<Identity> => {
    const { account, user } = AppState.getIdentity()
    try {
      return Promise.resolve({ id: user ? user.id : null, account, user })
    } catch (error) {
      return Promise.reject(error)
    }
  },
  logout: (): Promise<void> => {
    // called on user logout (logout button, checkError rejection, checkAuth rejection)
    // should handle current authentication data clean up (i.e. reset local storage)

    const request = generateFetchRequest({
      method: 'DELETE',
      path: 'users/sign_out',
    })

    return fetch(request).then((response) => {
      if (response.status != 204 && response.status != 200) {
        throw new Error('Error logging out')
      } else {
        AppState.reset()
        AppState.resetLastPage()
      }
    })
  },
}

export default authProvider
